// ────────────────────────────────────────────────────────────────────
// ReportsScreen — lists every Inspection as a deliverable report, filtered
// client-side by technique and a free-text search over report no / asset /
// project / client / inspector.
//
// A row navigates to the inspection detail. The per-row Download button hits
// GET /api/inspections/{id}/report.pdf, which renders report.html server-side
// with headless chromium — a real fetch that takes a few seconds and can fail,
// hence the busy/failed states in ReportDownloadButton.
//
// Still open: filter by client / plant / date.
// ────────────────────────────────────────────────────────────────────

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Search, X, Filter } from "lucide-react";
import { apiClient, downloadReportPdf } from "@/lib/api";

const TECHNIQUES = ["ALL", "UTM", "UT-CSCAN", "IMAGE", "PAUT", "RT-FILM"];

export default function ReportsScreen(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [tech, setTech] = useState("ALL");

  const inspQ = useQuery({
    queryKey: ["inspections", "all"],
    queryFn: () => apiClient.inspections(),
  });
  const all = inspQ.data ?? [];

  const filtered = useMemo(() => {
    let rows = all;
    if (tech !== "ALL") rows = rows.filter((r) => r.technique === tech);
    if (q.trim()) {
      const ql = q.trim().toLowerCase();
      rows = rows.filter((r) =>
        (r.doc_no || "").toLowerCase().includes(ql) ||
        (r.project_name || "").toLowerCase().includes(ql) ||
        (r.vessel_id || "").toLowerCase().includes(ql) ||
        (r.client || "").toLowerCase().includes(ql) ||
        (r.inspector_name || "").toLowerCase().includes(ql),
      );
    }
    return rows;
  }, [all, q, tech]);

  return (
    <div className="max-w-7xl mx-auto px-8 py-10">
      <header className="mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="font-mono uppercase mb-2"
               style={{ fontSize: 10.5, letterSpacing: ".24em", color: "var(--accent-deep)", fontWeight: 600 }}>
            / DELIVERABLE REPORTS
          </div>
          <h1 className="font-display font-medium m-0"
              style={{ fontSize: 36, color: "var(--ink-1)", letterSpacing: "-.02em" }}>
            Reports
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--ink-3)" }}>
            Inspection reports approved for client distribution & regulatory submission.
          </p>
        </div>
        <div className="text-right font-mono uppercase"
             style={{ fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-4)" }}>
          read-only<br />
          new reports generated by admin
        </div>
      </header>

      {/* Filter row */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md flex items-center"
             style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
          <Search size={13} style={{ color: "var(--ink-4)" }} className="ml-3 shrink-0" />
          <input
            placeholder="Search report no, asset, inspector…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 px-3 py-2 outline-none bg-transparent font-mono"
            style={{ fontSize: 12, color: "var(--ink-1)" }}
          />
          {q && (
            <button onClick={() => setQ("")} className="px-2 shrink-0"
                    style={{ color: "var(--ink-3)" }}>
              <X size={12} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 font-mono"
             style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--ink-4)" }}>
          <Filter size={11} className="mr-1" />
          {TECHNIQUES.map((t) => (
            <button
              key={t}
              onClick={() => setTech(t)}
              className="px-2.5 py-1.5 uppercase transition-colors"
              style={{
                background: tech === t ? "rgba(var(--accent-rgb) / 0.10)" : "transparent",
                color: tech === t ? "var(--accent-deep)" : "var(--ink-3)",
                border: `1px solid ${tech === t ? "var(--accent)" : "var(--hairline)"}`,
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 mb-6"
           style={{ border: "1px solid var(--hairline)" }}>
        <Stat label="REPORTS"   value={all.length} />
        <Stat label="MATCHED"   value={filtered.length} sub={tech === "ALL" && !q ? "all" : "filtered"} />
        <Stat label="UT-CSCAN"  value={all.filter((r) => r.technique === "UT-CSCAN").length} />
        <Stat label="IMAGE"     value={all.filter((r) => r.technique === "IMAGE").length} />
      </div>

      {/* Table */}
      <div style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
        <table className="w-full font-mono tabular-nums" style={{ fontSize: 12 }}>
          <thead>
            <tr style={{ background: "var(--paper)", borderBottom: "1px solid var(--hairline)" }}>
              <Th>REPORT NO.</Th>
              <Th>ASSET / PROJECT</Th>
              <Th>TECHNIQUE</Th>
              <Th>INSPECTOR</Th>
              <Th>DATE</Th>
              <Th>STATUS</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {inspQ.isPending && (
              <tr><td colSpan={7} className="text-center py-8" style={{ color: "var(--ink-3)" }}>
                Loading reports…
              </td></tr>
            )}
            {!inspQ.isPending && filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8" style={{ color: "var(--ink-3)" }}>
                No reports match.
              </td></tr>
            )}
            {filtered.map((r) => {
              const date = r.inspection_date || r.upload_date.slice(0, 10);
              return (
                <tr key={r.id}
                    onClick={() => navigate(`/inspections/${r.id}`)}
                    className="group hover:bg-[rgba(31,78,121,0.04)] cursor-pointer transition-colors"
                    style={{ borderBottom: "1px solid var(--hairline)" }}>
                  <Td>
                    <span className="flex items-center gap-2">
                      <FileText size={11} style={{ color: "var(--accent)" }} />
                      <span style={{ color: "var(--ink-1)", fontWeight: 600 }}>
                        {r.doc_no || `INSP-${r.id}`}
                      </span>
                    </span>
                  </Td>
                  <Td>
                    <div style={{ color: "var(--ink-1)" }}>{r.project_name || r.vessel_id || "—"}</div>
                    <div style={{ color: "var(--ink-4)", fontSize: 10 }}>{r.client}</div>
                  </Td>
                  <Td>
                    <span className="font-mono uppercase px-1.5 py-0.5"
                          style={{
                            fontSize: 9, letterSpacing: ".18em", color: "var(--accent-deep)",
                            border: "1px solid rgba(var(--accent-rgb) / 0.4)",
                            background: "rgba(var(--accent-rgb) / 0.06)",
                          }}>
                      {r.technique}
                    </span>
                  </Td>
                  <Td style={{ color: "var(--ink-2)" }}>{r.inspector_name || "—"}</Td>
                  <Td className="tabular-nums" style={{ color: "var(--ink-3)" }}>{date}</Td>
                  <Td>
                    <span className="font-mono uppercase"
                          style={{ fontSize: 9, letterSpacing: ".18em", color: "var(--ink-3)" }}>
                      {r.status}
                    </span>
                  </Td>
                  <Td>
                    <ReportDownloadButton inspectionId={r.id} />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ReportDownloadButton({ inspectionId }: { inspectionId: number }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function onClick(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();          // the row navigates on click
    setBusy(true);
    setFailed(false);
    try {
      await downloadReportPdf(inspectionId);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  // The failure states this button actually sees are RETRYABLE: 503 (the
  // renderer stumbled) and 429 (two renders already in flight — try again).
  // So the error sits BESIDE the button and never replaces it; swapping the
  // button out for a terminal "PDF failed" made the one recoverable path
  // unrecoverable without a page refresh.
  return (
    <span className="flex items-center justify-end gap-1.5">
      {failed && (
        <span role="alert" className="font-mono uppercase whitespace-nowrap"
              style={{ fontSize: 9, letterSpacing: ".16em", color: "var(--err)" }}>
          failed — retry
        </span>
      )}
      {busy && (
        // Progress must survive the mouse leaving the row: the button itself is
        // opacity-0 until hover, so without this the only sign of life during a
        // 5-45s render disappears the moment the cursor moves.
        <span role="status" className="font-mono uppercase whitespace-nowrap"
              style={{ fontSize: 9, letterSpacing: ".16em", color: "var(--ink-4)" }}>
          rendering…
        </span>
      )}
      <button
        onClick={onClick}
        disabled={busy}
        aria-label="Download PDF"
        title={busy ? "Rendering…" : failed ? "Download failed — click to retry"
                                            : "Download PDF"}
        // Stay visible off-hover whenever there is something to say.
        className={`p-1 ${busy || failed ? "" : "opacity-0 group-hover:opacity-100"}`}
        style={{ color: failed ? "var(--err)" : "var(--ink-3)" }}
      >
        <Download size={12} />
      </button>
    </span>
  );
}

function Stat({ label, value, sub }: { label: string; value: number; sub?: string }): JSX.Element {
  return (
    <div className="px-4 py-3 border-r last:border-r-0"
         style={{ borderColor: "var(--hairline)", background: "var(--paper-2)" }}>
      <div className="font-mono uppercase"
           style={{ fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-4)" }}>
        {label}
      </div>
      <div className="font-display font-bold tabular-nums leading-tight mt-0.5"
           style={{ fontSize: 22, color: "var(--ink-1)" }}>
        {value.toLocaleString()}
      </div>
      {sub && (
        <div className="font-mono uppercase mt-0.5"
             style={{ fontSize: 9, letterSpacing: ".18em", color: "var(--ink-4)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }): JSX.Element {
  return (
    <th className="text-left px-3 py-2 font-mono uppercase font-medium"
        style={{ fontSize: 9.5, letterSpacing: ".22em", color: "var(--ink-4)" }}>
      {children}
    </th>
  );
}

function Td(props: React.TdHTMLAttributes<HTMLTableCellElement>): JSX.Element {
  return <td {...props} className={`px-3 py-2.5 ${props.className ?? ""}`}>{props.children}</td>;
}
